import { Fragment, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useParams } from "react-router-dom";

import api from "../../services/api";
import DeletedBody from "../../components/ui/DeletedBody";
import { exactTime, formatDate, clockTime } from "../../utils/time";

const bySeq = (a, b) => a.seq - b.seq;
const DAY_MS = 86400000;
const dayKey = (iso) => (iso ? new Date(iso).toDateString() : "");

function dayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.round((new Date(new Date().toDateString()) - new Date(d.toDateString())) / DAY_MS);
  if (days === 0) return "Bugun";
  if (days === 1) return "Kecha";
  return formatDate(iso);
}

const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toLocaleUpperCase("uz"))
    .join("");

function hueOf(key) {
  let h = 0;
  for (const ch of String(key || "")) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/**
 * One person-to-person chat, read-only, oldest first -- the same log layout as
 * a discussion's history (ThreadDetailPage). STAFF ONLY: the API refuses
 * everyone else.
 */
const DirectChatDetailPage = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, error: "" });
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [older, setOlder] = useState({ loading: false, error: "" });
  const scrollRef = useRef(null);
  const openedAtEnd = useRef(false);
  const keepHeight = useRef(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await api.get(`/direct-chats/${id}/messages/`);
        if (!live) return;
        setChat(res.data.chat);
        setMessages([...(res.data.results || [])].sort(bySeq));
        setHasMore(Boolean(res.data.has_more));
        setState({ loading: false, error: "" });
        // Shown, so no longer "Yangi" for me (my own badge only).
        api.post(`/direct-chats/${id}/seen/`).catch(() => {});
      } catch {
        if (live) setState({ loading: false, error: "Suhbatni yuklab boʻlmadi." });
      }
    })();
    return () => {
      live = false;
    };
  }, [id]);

  const loadOlder = async () => {
    const oldest = messages[0];
    if (!oldest) return;
    keepHeight.current = scrollRef.current?.scrollHeight ?? null;
    setOlder({ loading: true, error: "" });
    try {
      const res = await api.get(`/direct-chats/${id}/messages/`, { params: { before_seq: oldest.seq } });
      setMessages((prev) => [...(res.data.results || []), ...prev].sort(bySeq));
      setHasMore(Boolean(res.data.has_more));
      setOlder({ loading: false, error: "" });
    } catch {
      setOlder({ loading: false, error: "Eski xabarlarni yuklab boʻlmadi." });
    }
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !messages.length) return;
    if (keepHeight.current != null) {
      el.scrollTop = el.scrollHeight - keepHeight.current;
      keepHeight.current = null;
      return;
    }
    if (!openedAtEnd.current) {
      el.scrollTop = el.scrollHeight;
      openedAtEnd.current = true;
    }
  }, [messages]);

  if (state.loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }
  if (state.error || !chat) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>{state.error || "Suhbat topilmadi."}</span>
        </div>
      </div>
    );
  }

  const people = [chat.member_a, chat.member_b];
  const title = `${chat.member_a?.full_name} ↔ ${chat.member_b?.full_name}`;

  return (
    <>
      <div className="ms-crumbs">
        <Link to="/shaxsiy-suhbatlar">Shaxsiy suhbatlar</Link>
        <Icon icon="tabler:chevron-right" />
        <span>{title}</span>
      </div>

      <div className="ms-recordgrid ms-recordgrid--half">
        <div className="ms-recordcol">
          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:info-circle" />
              <h2>Suhbat</h2>
            </div>
            <div className="ms-panel-body">
              <dl className="ms-detail">
                <div className="ms-detail-row">
                  <dt>Mijoz tizimi</dt>
                  <dd>
                    <Link className="ms-link" to={`/mijoz-tizimlari/${chat.client_id}`}>
                      {chat.client_name}
                    </Link>
                  </dd>
                </div>
                <div className="ms-detail-row">
                  <dt>Xabarlar</dt>
                  <dd>{chat.message_count ?? messages.length}</dd>
                </div>
                {people.map((p, i) => (
                  <div className="ms-detail-row" key={p?.id || i}>
                    <dt>{i === 0 ? "Ishtirokchi 1" : "Ishtirokchi 2"}</dt>
                    <dd>
                      {p?.full_name} <span className="ms-mono">{p?.pinfl}</span>
                    </dd>
                  </div>
                ))}
                <div className="ms-detail-row">
                  <dt>Ochilgan</dt>
                  <dd title={exactTime(chat.created_at)}>{formatDate(chat.created_at)}</dd>
                </div>
                <div className="ms-detail-row ms-detail-row--full">
                  <dt>Suhbat ID</dt>
                  <dd className="ms-mono">{chat.id}</dd>
                </div>
              </dl>
              <div className="ms-note" style={{ marginTop: 14 }}>
                <Icon icon="tabler:lock" /> Bu suhbat faqat MySuhbat xodimlariga koʻrinadi; mijoz kabinetida
                koʻrsatilmaydi. Panel undan faqat oʻqiydi.
              </div>
            </div>
          </div>
        </div>

        <div className="ms-recordcol">
          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:history" />
              <h2>Xabarlar tarixi</h2>
              <div className="ms-toolbar-spacer" />
              <span className="ms-badge ms-badge--neutral">
                {messages.length} / {chat.message_count ?? messages.length} ta
              </span>
            </div>

            {messages.length === 0 ? (
              <div className="ms-state">
                <Icon icon="tabler:message-off" />
                <span>Bu suhbatda hali xabar yoʻq</span>
              </div>
            ) : (
              <div className="ms-chat" ref={scrollRef}>
                {hasMore && (
                  <div className="ms-chat-more">
                    <button
                      type="button"
                      className="ms-btn ms-btn--ghost ms-btn--sm"
                      onClick={loadOlder}
                      disabled={older.loading}
                    >
                      <Icon icon="tabler:arrow-up" />
                      {older.loading ? "Yuklanmoqda…" : "Eskilarini yuklash"}
                    </button>
                  </div>
                )}
                {older.error && (
                  <div className="ms-state" style={{ padding: "12px 0" }}>
                    <Icon icon="tabler:alert-triangle" />
                    <span>{older.error}</span>
                  </div>
                )}

                {messages.map((m, i) => {
                  const prev = messages[i - 1];
                  const newDay = !prev || dayKey(prev.sent_at) !== dayKey(m.sent_at);
                  const opensRun = newDay || !prev || prev.author_id !== m.author_id;
                  return (
                    <Fragment key={m.id}>
                      {newDay && (
                        <div className="ms-chat-day">
                          <span>{dayLabel(m.sent_at)}</span>
                        </div>
                      )}
                      <div className={`ms-chat-row${opensRun ? " opens-run" : ""}`}>
                        <div
                          className="ms-chat-avatar"
                          aria-hidden="true"
                          style={opensRun ? { background: `hsl(${hueOf(m.author_id)} 52% 42%)` } : undefined}
                        >
                          {opensRun ? initialsOf(m.author_name) : ""}
                        </div>
                        <div className="ms-chat-col">
                          {opensRun && (
                            <div className="ms-chat-who">
                              <b>{m.author_name || "Nomaʼlum"}</b>
                            </div>
                          )}
                          <div className={`ms-chat-bubble${m.is_deleted ? " is-deleted" : ""}`}>
                            {m.is_deleted ? (
                              <DeletedBody url={`/direct-chats/${id}/messages/${m.seq}/reveal/`} />
                            ) : (
                              <span className="ms-chat-text">{m.body || "—"}</span>
                            )}
                            <span className="ms-chat-meta">
                              {m.is_deleted && (
                                <span className="ms-chat-flag" title={`Oʻchirilgan: ${exactTime(m.deleted_at)}`}>
                                  oʻchirilgan
                                </span>
                              )}
                              <span className="ms-chat-seq">#{m.seq}</span>
                              <time title={exactTime(m.sent_at)}>{clockTime(m.sent_at)}</time>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default DirectChatDetailPage;
